import { createTheme } from "@mui/material/styles";
import { globalFonts } from "./fonts";
import { convertToBreakpoints } from "../utils/fuilds";
import { createResponsiveTheme } from "../lib/ResponsiveScaler/utils";
import { BREAKPOINT_MOBILE } from "../constants";
import { pxToVw } from "../utils/common";

export const colors = {
  text: {
    v1: "#6c6763",
    v2: "#807A75",
    v3: "rgb(116, 111, 106)",
    v4: "#E2D8CE",
    v5: "#D9D9D9",
  },
  icons: {
    v1: "rgba(127, 122, 118, 1)",
  },
  background: {
    v1: "#1D1C1B",
  },
};

export const transitions: any = {
  standard: ".35s ease",
  linear: ".3s linear",
  join: (arr: string[] | string, animation = transitions.standard) =>
    (Array.isArray(arr) ? arr : [arr])
      .reduce((acc: [], cur) => {
        // @ts-ignore
        acc.push(`${cur} ${transitions.standard}`);
        return acc;
      }, [])
      .join(", "),
};

let baseTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
      xxxl: 2600,
    },
  },
});

export const theme = createTheme(baseTheme, {
  typography: {
    fontFamily:
      'DINOT, Volkolak, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    v1: {
      fontFamily: "Guton",
      color: colors.text.v1,
      lineHeight: 1,
      fontSize: pxToVw(32),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "15px" },
    },
    v2: {
      fontFamily: "Guton",
      color: colors.text.v2,
      lineHeight: 1,
      fontSize: pxToVw(32),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "10px" },
    },
    v2_special: {
      fontFamily: "Guton",
      color: colors.text.v2,
      lineHeight: 1,
      fontSize: pxToVw(32),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "12px" },
    },
    v3: {
      fontFamily: "Guton",
      color: colors.text.v3,
      letterSpacing: "2px",
      lineHeight: 1,
      fontSize: pxToVw(48),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "13px" },
    },
    v4: {
      fontFamily: "Guton",
      color: colors.text.v3,
      lineHeight: 1,
      fontSize: pxToVw(32),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "20px" },
    },
    v5: {
      fontFamily: "Guton",
      color: colors.text.v2,
      lineHeight: 1.3,
      fontSize: pxToVw(24),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "14px" },
    },
    v5_special: {
      fontFamily: "Guton",
      color: colors.text.v2,
      lineHeight: 1.3,
      fontSize: pxToVw(24),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "8px" },
    },
    v6: {
      fontFamily: "Guton",
      color: colors.text.v4,
      lineHeight: 1.5,
      fontSize: pxToVw(64),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "32px" },
    },
    v7: {
      fontFamily: "Guton",
      color: colors.text.v3,
      lineHeight: 1.5,
      letterSpacing: "10px",
      textTransform: "uppercase",
      fontSize: pxToVw(40),
      [baseTheme.breakpoints.down(BREAKPOINT_MOBILE)]: { fontSize: "13px" },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: globalFonts as any,
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          // borderColor: 'red'
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          ".MuiFormLabel-root": {
            "&.Mui-focused, &.MuiFormLabel-filled": {
              color: colors.text.v3,
            },
          },
          ".MuiFormHelperText-root": {
            position: "absolute",
            width: "100%",
            top: "100%",
            lineHeight: 1,
            marginTop: "1px",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          position: "relative",
          borderRadius: "15px",
          backgroundColor: "rgb(39, 37, 36)",
          color: colors.text.v3,

          "&::placeholder": {
            // color: "red",
            opacity: 1,
          },

          "& .MuiOutlinedInput-notchedOutline": {
            // borderColor: "rgba(255, 255, 255, 0.23)",
          },

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.23)",
          },

          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.text.v3,
            color: colors.text.v3,
          },

          /* replace error-styles */
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.23)",
          },
          "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.23)",
          },
          "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.text.v3,
          },
        },

        input: {
          color: colors.text.v2,
          ...convertToBreakpoints(baseTheme, {
            xxxl: { padding: "26px 14px" },
          }),
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        /* @ts-ignore */
        root: ({ theme }) => ({
          ...(theme.typography as any).v2,
          "&.MuiInputLabel-shrink": {
            /* @TODO redundant ??? */
            // borderRadius: '10px',
            // background: colors.background.v1,
            padding: "1px 5px",
            transform: "translate(14px, -9px) scale(0.55)",
          },
          "&.Mui-focused": {
            color: colors.text.v3,
          },
          "&.Mui-error": {
            color: colors.text.v2,
          },
          ...convertToBreakpoints(baseTheme, {
            xxxl: { transform: "translate(14px, 26px) scale(1)" },
          }),
        }),
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: colors.text.v3,
          ".MuiCheckbox-root": {
            color: colors.text.v2,
          },
          ".MuiSvgIcon-root": {
            borderRadius: "5px",
          },
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.text.v2,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "transparent",
          borderRadius: 0,
          maxWidth: "1664px",
          // borderRadius: "10px",
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          borderRadius: "15px",
          background: colors.background.v1,
          // padding: "35px",
        },
      },
    },

    MuiTypography: {
      defaultProps: { variantMapping: { v1: "span" } },
    },
  },
});
